import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ClipboardList, Layers, BarChart3 } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// Runs `callback` every `ms`, but only while `ref`'s element is on screen.
// Keeps the looping infographics from re-rendering while scrolled away.
const useIntervalWhileVisible = (ref, callback, ms) => {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let interval = null;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && interval === null) {
        interval = setInterval(() => cbRef.current(), ms);
      } else if (!entry.isIntersecting && interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (interval !== null) clearInterval(interval);
    };
  }, [ref, ms]);
};

// ─── Card 1: Resume Upload Infographic ───
// Animated resume upload that extracts profile fields
const ResumeUploadInfographic = () => {
  const rootRef = useRef(null);
  const [phase, setPhase] = useState(0);
  // 0 = idle, 1 = uploading, 2 = parsing, 3-6 = extracting fields, 7 = complete

  const extractedFields = [
    { label: "Name", value: "Jane Smith" },
    { label: "Role", value: "Software Engineer" },
    { label: "Experience", value: "3-5 years" },
    { label: "Skills", value: "React, Node, AWS" },
  ];

  useIntervalWhileVisible(rootRef, () => setPhase((prev) => (prev + 1) % 9), 1400);

  const isUploading = phase === 1;
  const isParsing = phase === 2;
  const extractCount = phase >= 3 ? Math.min(phase - 2, extractedFields.length) : 0;
  const isComplete = phase >= 7;
  const uploadProgress = isUploading ? 100 : phase >= 2 ? 100 : 0;

  return (
    <div
      ref={rootRef}
      style={{
        background: "var(--c-bg)",
        borderRadius: "1rem",
        padding: "1.25rem",
        height: 220,
        display: "flex",
        flexDirection: "column",
        gap: "0.6rem",
      }}
    >
      {/* Upload area */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          padding: "0.5rem 0.75rem",
          borderRadius: 8,
          border: `1.5px dashed ${phase >= 1 ? "var(--c-primary)" : "var(--c-border-subtle)"}`,
          background: phase >= 1 ? "rgba(26, 188, 156, 0.04)" : "#fff",
          transition: "all 0.4s ease",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={phase >= 1 ? "var(--c-primary)" : "var(--c-text-secondary)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "stroke 0.3s ease", flexShrink: 0 }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--font-heading)", fontSize: "0.7rem", fontWeight: 600, color: phase >= 1 ? "var(--c-dark)" : "var(--c-text-secondary)", transition: "color 0.3s ease" }}>
            {phase === 0 ? "Drop your resume" : "resume_jane_smith.pdf"}
          </div>
          {isUploading && (
            <div style={{ height: 3, borderRadius: 2, background: "var(--c-border-subtle)", marginTop: 4, overflow: "hidden" }}>
              <div style={{ width: `${uploadProgress}%`, height: "100%", borderRadius: 2, background: "var(--c-primary)", transition: "width 0.8s ease" }} />
            </div>
          )}
          {isParsing && (
            <div style={{ fontFamily: "var(--font-data)", fontSize: "0.6rem", color: "var(--c-primary)", marginTop: 2 }}>
              Parsing…
            </div>
          )}
        </div>
      </div>

      {/* Extracted fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
        {extractedFields.map((field, i) => {
          const isExtracted = i < extractCount;
          return (
            <div
              key={field.label}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.4rem 0.75rem",
                borderRadius: 8,
                border: `1px solid ${isExtracted ? "rgba(26, 188, 156, 0.3)" : "var(--c-border-subtle)"}`,
                background: isExtracted ? "rgba(26, 188, 156, 0.04)" : "#fff",
                transition: "all 0.3s ease",
              }}
            >
              <span style={{ fontFamily: "var(--font-heading)", fontSize: "0.72rem", fontWeight: 500, color: "var(--c-text-secondary)" }}>
                {field.label}
              </span>
              <span style={{ fontFamily: "var(--font-data)", fontSize: "0.7rem", fontWeight: 500, color: isExtracted ? "var(--c-dark)" : "transparent", transition: "color 0.3s ease" }}>
                {isExtracted ? field.value : "\u2014"}
              </span>
            </div>
          );
        })}
      </div>

      {/* Complete indicator */}
      {isComplete && (
        <div
          style={{
            textAlign: "center",
            fontFamily: "var(--font-data)",
            fontSize: "0.7rem",
            fontWeight: 500,
            color: "var(--c-primary)",
            letterSpacing: "0.05em",
            animation: "item-enter 0.4s ease",
          }}
        >
          Profile Ready
        </div>
      )}
    </div>
  );
};

// ─── Card 2: Simplified Swipe Demo ───
// Mini version of the actual JobCard with swipe animation
const SimpleSwipeDemo = () => {
  const [cards, setCards] = useState([
    {
      title: "Senior Frontend Dev",
      company: "Northwind Pay",
      companyDesc: "Payments infrastructure for online business",
      type: "Full-time",
      location: "San Francisco, CA",
      salary: "$180k-$220k/yr",
      posted: "2d ago",
      categories: ["Frontend", "React", "TypeScript"],
      description: "About the role\nWe're looking for a Senior Frontend Developer to build and maintain high-performance web applications powering our payments platform. You'll work closely with product and design to ship polished features at scale.\n\nWhat you'll do\n• Architect and build responsive, accessible UIs\n• Collaborate with backend teams on API design",
    },
    {
      title: "Full Stack Engineer",
      company: "Lumen Works",
      companyDesc: "The connected workspace for modern teams",
      type: "Full-time",
      location: "Remote",
      salary: "$160k-$200k/yr",
      posted: "5d ago",
      categories: ["Full Stack", "Node.js", "React"],
      description: "About the role\nJoin a small, high-impact team building features across the entire stack for millions of users. You'll ship product end-to-end — from database schema to pixel-perfect UI.\n\nWhat you'll do\n• Design and implement full-stack features\n• Improve performance and reliability at scale",
    },
    {
      title: "React Developer",
      company: "Atlas Deploy",
      companyDesc: "Build. Preview. Ship.",
      type: "Full-time",
      location: "Remote",
      salary: "$150k-$190k/yr",
      posted: "1d ago",
      categories: ["Frontend", "Next.js", "Edge"],
      description: "About the role\nHelp define the future of frontend development tools used by thousands of teams worldwide. You'll build the platform developers rely on to deploy and scale applications.\n\nWhat you'll do\n• Build and optimize the deployment dashboard\n• Develop new deployment and edge features",
    },
  ]);
  const [swipeDir, setSwipeDir] = useState(null);
  const rootRef = useRef(null);

  useIntervalWhileVisible(rootRef, () => {
    const dir = Math.random() > 0.3 ? "right" : "left";
    setSwipeDir(dir);
    setTimeout(() => {
      setCards((prev) => {
        const next = [...prev];
        next.push(next.shift());
        return next;
      });
      setSwipeDir(null);
    }, 500);
  }, 2800);

  return (
    <div ref={rootRef} style={{ position: "relative", height: 300 }}>
      {[...cards].reverse().map((card, i) => {
        const pos = cards.length - 1 - i;
        const isTop = pos === 0;
        const swiping = isTop && swipeDir;

        return (
          <div
            key={card.title}
            style={{
              position: "absolute",
              width: "100%",
              height: 288,
              background: "#fff",
              borderRadius: 14,
              boxShadow: isTop
                ? "0 4px 24px rgba(0,0,0,0.10), 0 1.5px 4px rgba(0,0,0,0.08)"
                : "0 2px 8px rgba(0,0,0,0.06)",
              transition: swiping
                ? "all 0.5s ease"
                : "all 0.6s var(--ease-bounce)",
              top: pos * 6,
              transform: swiping
                ? `translateX(${swipeDir === "right" ? 120 : -120}%) rotate(${swipeDir === "right" ? 10 : -10}deg)`
                : `scale(${1 - pos * 0.04})`,
              opacity: swiping ? 0 : 1 - pos * 0.12,
              zIndex: cards.length - pos,
              overflow: "hidden",
            }}
          >
            {/* Swipe color overlay */}
            {swiping && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    swipeDir === "right"
                      ? "rgba(0, 230, 118, 0.15)"
                      : "rgba(255, 77, 77, 0.12)",
                  zIndex: 1,
                  borderRadius: "inherit",
                }}
              />
            )}

            <div style={{ padding: "0.9rem 1rem 1rem", position: "relative", height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
              {/* Job type chip — top right */}
              <div
                style={{
                  position: "absolute",
                  top: "0.75rem",
                  right: "0.75rem",
                  fontFamily: "var(--font-heading)",
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  color: "#fff",
                  background: "var(--c-primary)",
                  padding: "0.15rem 0.5rem",
                  borderRadius: 6,
                }}
              >
                {card.type}
              </div>

              {/* Title + Company */}
              <div
                style={{
                  fontFamily: "var(--font-heading)",
                  fontWeight: 800,
                  fontSize: "0.9rem",
                  color: "#1a237e",
                  lineHeight: 1.3,
                  marginBottom: 2,
                  paddingRight: "4rem",
                }}
              >
                {card.title}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "0.4rem",
                  marginBottom: "0.5rem",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  {card.company}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-data)",
                    fontSize: "0.55rem",
                    color: "var(--c-text-secondary)",
                    opacity: 0.6,
                  }}
                >
                  — {card.companyDesc}
                </span>
              </div>

              {/* Category chips */}
              <div
                style={{
                  display: "flex",
                  gap: "0.3rem",
                  marginBottom: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {card.categories.map((cat) => (
                  <span
                    key={cat}
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "0.6rem",
                      fontWeight: 500,
                      color: "var(--c-primary)",
                      background: "var(--c-primary-light)",
                      border: "1px solid rgba(26, 188, 156, 0.3)",
                      padding: "0.1rem 0.45rem",
                      borderRadius: 10,
                    }}
                  >
                    {cat}
                  </span>
                ))}
              </div>

              {/* Location + Salary + Posted row */}
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "0.5rem",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    fontFamily: "var(--font-heading)",
                    fontSize: "0.65rem",
                    color: "var(--c-text-secondary)",
                  }}
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {card.location}
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    fontFamily: "var(--font-heading)",
                    fontSize: "0.65rem",
                    color: "var(--c-text-secondary)",
                  }}
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  {card.salary}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "0.6rem",
                    color: "var(--c-text-secondary)",
                    opacity: 0.7,
                  }}
                >
                  {card.posted}
                </span>
              </div>

              {/* Job description */}
              <div
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "0.6rem",
                  lineHeight: 1.55,
                  color: "var(--c-text-secondary)",
                  opacity: 0.75,
                  borderTop: "1px solid var(--c-border-subtle)",
                  paddingTop: "0.5rem",
                  flex: 1,
                  overflow: "hidden",
                  whiteSpace: "pre-line",
                }}
              >
                {card.description}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Card 3: Mini Kanban ───
// Shows applications flowing through pipeline stages
const MiniKanban = () => {
  const stages = ["Applying", "Applied", "Screen", "Interview", "Offer"];
  const [cards, setCards] = useState([
    { id: 1, stage: 0, company: "Meridian Labs" },
    { id: 2, stage: 1, company: "Northwind Pay" },
    { id: 3, stage: 1, company: "Helios Systems" },
    { id: 4, stage: 2, company: "Lumen Works" },
    { id: 5, stage: 3, company: "Atlas Deploy" },
  ]);

  const rootRef = useRef(null);

  useIntervalWhileVisible(rootRef, () => {
    setCards((prev) => {
      const moveable = prev.filter((c) => c.stage < stages.length - 1);
      if (moveable.length === 0) {
        return [
          { id: 1, stage: 0, company: "Meridian Labs" },
          { id: 2, stage: 0, company: "Northwind Pay" },
          { id: 3, stage: 1, company: "Helios Systems" },
          { id: 4, stage: 1, company: "Lumen Works" },
          { id: 5, stage: 2, company: "Atlas Deploy" },
        ];
      }
      const card = moveable[Math.floor(Math.random() * moveable.length)];
      return prev.map((c) =>
        c.id === card.id ? { ...c, stage: c.stage + 1 } : c
      );
    });
  }, 2000);

  return (
    <div
      ref={rootRef}
      style={{
        height: 220,
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
    >
      {/* Stage headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${stages.length}, 1fr)`,
          gap: 4,
        }}
      >
        {stages.map((stage) => (
          <div
            key={stage}
            style={{
              fontFamily: "var(--font-data)",
              fontSize: "0.55rem",
              fontWeight: 500,
              color: "var(--c-text-secondary)",
              textAlign: "center",
              letterSpacing: "0.03em",
              paddingBottom: 4,
            }}
          >
            {stage}
          </div>
        ))}
      </div>

      {/* Kanban columns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${stages.length}, 1fr)`,
          gap: 4,
          flex: 1,
        }}
      >
        {stages.map((stage, stageIdx) => {
          const stageCards = cards.filter((c) => c.stage === stageIdx);
          return (
            <div
              key={stage}
              style={{
                background: "var(--c-bg)",
                borderRadius: 8,
                border: "1px solid var(--c-border-subtle)",
                padding: "0.3rem",
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minHeight: 0,
              }}
            >
              {stageCards.map((card) => (
                <div
                  key={card.id}
                  style={{
                    background: "#fff",
                    borderRadius: 5,
                    padding: "0.3rem 0.35rem",
                    border: "1px solid var(--c-border-subtle)",
                    transition: "all 0.5s var(--ease-magnetic)",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    borderLeft:
                      card.stage >= 4 ? "2px solid var(--c-primary)" : "none",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "0.55rem",
                      fontWeight: 600,
                      color:
                        card.stage >= 4
                          ? "var(--c-primary)"
                          : "var(--c-dark)",
                      lineHeight: 1.2,
                    }}
                  >
                    {card.company}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          paddingTop: "0.25rem",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-data)",
            fontSize: "0.65rem",
            color: "var(--c-text-secondary)",
            whiteSpace: "nowrap",
          }}
        >
          {cards.filter((c) => c.stage >= 4).length} offers
        </div>
        <div
          style={{
            flex: 1,
            height: 3,
            borderRadius: 2,
            background: "var(--c-border-subtle)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${(cards.filter((c) => c.stage >= 4).length / cards.length) * 100}%`,
              height: "100%",
              borderRadius: 2,
              background: "var(--c-primary)",
              transition: "width 0.5s ease",
            }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Main Features Section ───
const CinematicFeatures = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".cinematic-features__card",
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".cinematic-features__grid",
            start: "top 85%",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="cinematic-features" id="features" ref={sectionRef}>
      <div className="cinematic-features__inner">
        <div className="cinematic-features__header">
          <p className="cinematic-features__label">How It Works</p>
          <h2 className="cinematic-features__title">
            Three steps and you're {" "}
            <span className="cinematic-features__title-drama">done.</span>
          </h2>
        </div>

        <div className="cinematic-features__grid">
          <div className="cinematic-features__card">
            <div className="cinematic-features__card-header">
              <div className="cinematic-features__card-step">
                <ClipboardList
                  size={16}
                  style={{ color: "var(--c-primary)" }}
                />
                <span>Step 01</span>
              </div>
              <h3 className="cinematic-features__card-title">
                Upload Your Resume
              </h3>
              <p className="cinematic-features__card-desc">
                Drop your resume and we'll extract your experience, skills, and
                preferences automatically. Takes about 30 seconds.
              </p>
            </div>
            <ResumeUploadInfographic />
          </div>

          <div className="cinematic-features__card">
            <div className="cinematic-features__card-header">
              <div className="cinematic-features__card-step">
                <Layers size={16} style={{ color: "var(--c-primary)" }} />
                <span>Step 02</span>
              </div>
              <h3 className="cinematic-features__card-title">
                Swipe on Jobs You Love
              </h3>
              <p className="cinematic-features__card-desc">
                We match you with roles that fit. Swipe right to apply, left to
                skip — we handle the rest.
              </p>
            </div>
            <SimpleSwipeDemo />
          </div>

          <div className="cinematic-features__card">
            <div className="cinematic-features__card-header">
              <div className="cinematic-features__card-step">
                <BarChart3 size={16} style={{ color: "var(--c-primary)" }} />
                <span>Step 03</span>
              </div>
              <h3 className="cinematic-features__card-title">
                Track Your Applications
              </h3>
              <p className="cinematic-features__card-desc">
                Monitor every application from submitted to offer in your
                personal dashboard.
              </p>
            </div>
            <MiniKanban />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CinematicFeatures;
